import type { ServerSideSelectionState } from '@civilgrid/ui/components';
import { useCallback, useState } from 'react';
import type { BulkPlanStatusResponse, SummariesSearchPayload } from '~/api/intelligenceSummariesV3';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { IntelligenceSummary, PlanStatus } from '~/common/types';
import { Notify } from '~/components2/Notify';
import { useBulkUpdatePlanStatus } from './queries';
import { toBulkSelection } from './useBulkEditConstructionDates';

/** Prefill: exactly one selected row seeds the select with its current status; otherwise unset. */
export function getPrefillPlanStatus(
  selection: ServerSideSelectionState,
  getSummaryById: (id: string) => IntelligenceSummary | undefined,
): PlanStatus | '' {
  if (selection.mode === 'partial' && selection.selectedIds.length === 1) {
    return getSummaryById(selection.selectedIds[0])?.project.planStatus ?? '';
  }
  return '';
}

export type UseBulkEditPlanStatusParams = {
  organizationId: string;
  /** Row lookup for the single-selection prefill (current page only, like the dates dialog). */
  getSummaryById: (id: string) => IntelligenceSummary | undefined;
  /** The table's current filters/sort, sent when the selection is select-all. */
  buildSearchForBulk: () => SummariesSearchPayload;
  /** Called after a successful apply with the endpoint result (invalidate + clear selection; no polling). */
  onApplied: (result: BulkPlanStatusResponse) => void;
};

/**
 * Dialog state + submit flow for the "Edit Plan Status" bulk action, modeled on
 * useBulkEditConstructionDates: one status value applied to every selected row.
 */
export function useBulkEditPlanStatus({
  organizationId,
  getSummaryById,
  buildSearchForBulk,
  onApplied,
}: UseBulkEditPlanStatusParams) {
  const [pendingSelection, setPendingSelection] = useState<ServerSideSelectionState | null>(null);
  const [prefill, setPrefill] = useState<PlanStatus | ''>('');
  const [planStatus, setPlanStatus] = useState<PlanStatus | ''>('');

  const mutation = useBulkUpdatePlanStatus(organizationId);

  const open = useCallback(
    (selection: ServerSideSelectionState) => {
      setPendingSelection(selection);
      const prefillValue = getPrefillPlanStatus(selection, getSummaryById);
      setPrefill(prefillValue);
      setPlanStatus(prefillValue);

      capturePosthogEvent(POSTHOG_EVENTS.conflictsSummaryEditPlanStatusDialogOpened, {
        organization_id: organizationId,
        selection_mode: selection.mode,
        selection_count: selection.mode === 'partial' ? selection.selectedIds.length : null,
        excluded_count: selection.mode === 'all' ? selection.excludedIds.length : 0,
      });
    },
    [getSummaryById, organizationId],
  );

  const close = useCallback(() => {
    setPendingSelection(null);
  }, []);

  const selectPlanStatus = useCallback(
    (next: PlanStatus) => {
      capturePosthogEvent(POSTHOG_EVENTS.conflictsSummaryEditPlanStatusSelected, {
        organization_id: organizationId,
        to_status: next,
      });
      setPlanStatus(next);
    },
    [organizationId],
  );

  // A single-row prefill keeps Apply disabled until the status actually changes.
  const canSubmit = planStatus !== '' && planStatus !== prefill;

  const onSubmit = useCallback(() => {
    if (!organizationId || !pendingSelection || planStatus === '') return;

    mutation.mutate(
      { selection: toBulkSelection(pendingSelection, buildSearchForBulk), updates: { planStatus } },
      {
        onSuccess: (result) => {
          capturePosthogEvent(POSTHOG_EVENTS.conflictsSummaryPlanStatusBulkUpdated, {
            organization_id: organizationId,
            selection_mode: pendingSelection.mode,
            row_count: result.updatedCount,
            to_status: planStatus,
          });
          setPendingSelection(null);
          onApplied(result);
        },
        // The dialog stays open on failure so the user can retry.
        onError: (err) => {
          Notify.error({
            message: 'An error occurred while updating plan status. Please contact support.',
          });
          capturePosthogEvent(POSTHOG_EVENTS.conflictsSummaryPlanStatusBulkUpdateFailed, {
            organization_id: organizationId,
            selection_mode: pendingSelection.mode,
            error_message: err instanceof Error ? err.message : 'unknown',
          });
        },
      },
    );
  }, [organizationId, pendingSelection, planStatus, buildSearchForBulk, mutation, onApplied]);

  return {
    isOpen: pendingSelection !== null,
    open,
    close,
    planStatus,
    setPlanStatus: selectPlanStatus,
    canSubmit,
    onSubmit,
    isPending: mutation.isPending,
  };
}
